import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { OrganizationGroup, GroupType, GroupFormData } from '../types/groups';

import { API_URL } from '../constants/urls';

interface GroupsManagementProps {
  organizationId: string;
  groupType?: GroupType;
  canEdit: boolean;
  canDelete: boolean;
  onGroupsChange?: () => void;
}

const getInitialFormData = (defaultType?: GroupType): GroupFormData => ({
  name: '',
  description: '',
  group_type: defaultType || 'segment',
  color: '',
  icon: '',
  is_active: true,
  display_order: 0,
});

const GROUP_TYPE_OPTIONS: { value: GroupType; label: string }[] = [
  { value: 'segment', label: 'Segments' },
  { value: 'proposition', label: 'Propositions' },
];

const COLOR_PRESETS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Yellow
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#6B7280', // Gray
  '#F97316', // Orange
];

export default function GroupsManagement({
  organizationId,
  groupType,
  canEdit,
  canDelete,
  onGroupsChange,
}: GroupsManagementProps) {
  const [groups, setGroups] = useState<OrganizationGroup[]>([]);
  const [open, setOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<OrganizationGroup | null>(
    null,
  );
  const [formData, setFormData] = useState<GroupFormData>(
    getInitialFormData(groupType),
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (organizationId) {
      fetchGroups();
    }
  }, [organizationId, groupType]);

  const fetchGroups = async () => {
    if (!organizationId) {
      // console.warn removed
      return;
    }
    try {
      let url = `${API_URL}/groups?organization_id=${organizationId}`;
      if (groupType) {
        url += `&group_type=${groupType}`;
      }
      const response = await axios.get(url);
      setGroups(response.data);
    } catch (error) {
      // console.error removed
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (editingGroup) {
        // Update existing group
        await axios.put(
          `${API_URL}/groups/${editingGroup.id}?organization_id=${organizationId}`,
          formData,
        );
      } else {
        // Create new group
        await axios.post(
          `${API_URL}/groups?organization_id=${organizationId}`,
          formData,
        );
      }
      setOpen(false);
      setEditingGroup(null);
      setFormData(getInitialFormData(groupType));
      fetchGroups();
      if (onGroupsChange) {
        onGroupsChange();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (group: OrganizationGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      group_type: group.group_type,
      color: group.color || '',
      icon: group.icon || '',
      is_active: group.is_active,
      display_order: group.display_order,
    });
    setOpen(true);
  };

  const handleDelete = async (group: OrganizationGroup) => {
    const segmentCount = group.segment_count || 0;
    const propositionCount = group.proposition_count || 0;
    const hasRelatedItems = segmentCount > 0 || propositionCount > 0;

    let confirmMessage = 'Are you sure you want to delete this group?';
    if (hasRelatedItems) {
      confirmMessage = `This group is used by ${segmentCount} segment(s) and ${propositionCount} proposition(s).\n\nDeleting this group will remove it from all related items.\n\nAre you sure you want to continue?`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API_URL}/groups/${group.id}?organization_id=${organizationId}`,
      );

      // Show success message with affected items count
      if (
        response.data.affected_segments > 0 ||
        response.data.affected_propositions > 0
      ) {
        alert(
          `Group deleted successfully. Removed from ${response.data.affected_segments} segment(s) and ${response.data.affected_propositions} proposition(s).`,
        );
      }

      fetchGroups();
      if (onGroupsChange) {
        onGroupsChange();
      }
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete group');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingGroup(null);
    setFormData(getInitialFormData(groupType));
    setError(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900">Manage Groups</h2>
        {canEdit && (
          <button
            onClick={() => setOpen(true)}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Group
          </button>
        )}
      </div>

      {/* Groups List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {groups.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No groups found.</p>
            {canEdit && (
              <p className="mt-2 text-sm text-gray-400">
                Create your first group to organize your segments and
                propositions.
              </p>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {groups.map((group) => (
              <li key={group.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {group.color && (
                      <div
                        className="w-4 h-4 rounded-full mr-3 flex-shrink-0"
                        style={{ backgroundColor: group.color }}
                      />
                    )}
                    <div>
                      <div className="flex items-center">
                        <h3 className="text-sm font-medium text-gray-900">
                          {group.name}
                        </h3>
                        <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          {group.group_type === 'segment'
                            ? 'Segments'
                            : 'Propositions'}
                        </span>
                      </div>
                      {group.description && (
                        <p className="text-sm text-gray-500 mt-1">
                          {group.description}
                        </p>
                      )}
                      <div className="mt-2 flex items-center text-xs text-gray-400 space-x-4">
                        {group.segment_count !== undefined &&
                          group.segment_count > 0 && (
                            <span>{group.segment_count} segments</span>
                          )}
                        {group.proposition_count !== undefined &&
                          group.proposition_count > 0 && (
                            <span>{group.proposition_count} propositions</span>
                          )}
                        {group.created_by_name && (
                          <span>Created by {group.created_by_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!group.is_active && (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        Inactive
                      </span>
                    )}
                    {canEdit && (
                      <button
                        onClick={() => handleEdit(group)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => handleDelete(group)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create/Edit Modal */}
      {open && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500/75 transition-opacity"
              onClick={handleClose}
            />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingGroup ? 'Edit Group' : 'Create New Group'}
                    </h3>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                      {error}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        rows={3}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Group Type *
                      </label>
                      <select
                        required
                        value={formData.group_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            group_type: e.target.value as GroupType,
                          })
                        }
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        {GROUP_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Color
                      </label>
                      <div className="mt-2 flex items-center space-x-2">
                        {COLOR_PRESETS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setFormData({ ...formData, color })}
                            className={`w-8 h-8 rounded-full border-2 ${
                              formData.color === color
                                ? 'border-gray-900'
                                : 'border-gray-300'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                        <input
                          type="color"
                          value={formData.color || '#3B82F6'}
                          onChange={(e) =>
                            setFormData({ ...formData, color: e.target.value })
                          }
                          className="ml-2 h-8 w-16"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Display Order
                      </label>
                      <input
                        type="number"
                        value={formData.display_order}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            display_order: parseInt(e.target.value) || 0,
                          })
                        }
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_active: e.target.checked,
                          })
                        }
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="is_active"
                        className="ml-2 block text-sm text-gray-900"
                      >
                        Active
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : editingGroup ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
